import { useCallback, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Package,
  QrCode,
  Truck,
  Inbox,
  Plus,
  AlertTriangle,
  ScanLine,
  RefreshCw,
  ClipboardCheck,
  Send,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/app/components/ui/collapsible';
import { cn } from '@/app/components/ui/utils';
import { toast } from '@/app/components/ui/simple-toaster';
import { handleApiError } from '@/lib/api-client';
import { useGearBoard } from '@/hooks/use-gear-board';
import {
  useGearRequests,
  useScanGearRequest,
  useDeployGearRequest,
  useReceiveGearRequest,
  useCreateGearRequest,
  useConfirmSupervisorRequest,
  useFulfillGearRequest,
} from '@/hooks/use-gear-requests';
import { useProjects } from '@/hooks/use-projects';
import { usePermissions } from '@/hooks/use-permissions';
import type {
  GearBoardCategory,
  GearBoardProjectNode,
  GearBoardStatus,
  GearRequest,
  GearRequestLine,
  GearRequestStatus,
  AssetType,
  GearRequestPriority,
  CreateGearRequestPayload,
  DeployGearRequestPayload,
} from '@/lib/types';

// Kept in sync with the palette on the gear request list/detail pages so a
// status reads the same colour everywhere. The two board-only statuses take the
// colour of their closest request status: `packing` sits alongside `requested`,
// and `ready` maps to `fulfilled`.
const boardStatusColors: Record<GearBoardStatus, string> = {
  pending_confirmation: 'bg-amber-100 text-amber-800',
  requested: 'bg-yellow-100 text-yellow-800',
  packing: 'bg-yellow-100 text-yellow-800',
  ready: 'bg-blue-100 text-blue-800',
  in_transit: 'bg-purple-100 text-purple-800',
  partially_received: 'bg-orange-100 text-orange-800',
  received: 'bg-green-100 text-green-800',
  returned: 'bg-teal-100 text-teal-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

const boardStatusLabels: Record<GearBoardStatus, string> = {
  pending_confirmation: 'Pending Confirmation',
  requested: 'Requested',
  packing: 'Packing',
  ready: 'Ready to Ship',
  in_transit: 'In Transit',
  partially_received: 'Partially Received',
  received: 'Received',
  returned: 'Returned to Inventory',
  cancelled: 'Cancelled',
};

// Request statuses that can still have actionable board rows (scan/deploy/receive/confirm).
const ACTIONABLE_REQUEST_STATUSES: GearRequestStatus[] = [
  'pending_confirmation',
  'requested',
  'fulfilled',
  'in_transit',
  'partially_received',
];

const assetCategoryOptions: AssetType[] = [
  'carabiner',
  'harness',
  'rope',
  'lanyard',
  'sling',
  'descender',
  'ascender',
  'pulley',
  'helmet',
  'anchor',
  'anchor_strap',
  'connector',
  'fall_arrester',
  'fall_arrest_accessory',
  'mobile_fall_arrester',
  'rigging_plate',
  'rope_clamp',
  'swivel',
  'other',
];

interface BoardFilters {
  project: string;
  status: GearRequestStatus | '';
  board_status: GearBoardStatus | '';
  priority: string;
  needed_by_before: string;
  needed_by_after: string;
}

const EMPTY_FILTERS: BoardFilters = {
  project: '',
  status: '',
  board_status: '',
  priority: '',
  needed_by_before: '',
  needed_by_after: '',
};

function formatCategory(category: string): string {
  return category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function matchesCategory(
  line: GearRequestLine,
  category: GearBoardCategory
): boolean {
  return (
    line.description === category.description &&
    (line.asset_category || '') === category.asset_category &&
    (line.gear_model ?? null) === category.gear_model.id
  );
}

function findMatchingRequests(
  requests: GearRequest[],
  node: GearBoardProjectNode,
  category: GearBoardCategory,
  statuses?: GearRequestStatus[]
): GearRequest[] {
  return requests.filter((req) => {
    if (req.project !== node.project.id) return false;
    if (statuses && !statuses.includes(req.status)) return false;
    return req.lines.some((line) => matchesCategory(line, category));
  });
}

function findFirstMatchingRequest(
  requests: GearRequest[],
  node: GearBoardProjectNode,
  category: GearBoardCategory,
  statuses?: GearRequestStatus[]
): GearRequest | null {
  return findMatchingRequests(requests, node, category, statuses)[0] ?? null;
}

function findMatchingLine(
  request: GearRequest,
  category: GearBoardCategory
): GearRequestLine | null {
  return (
    request.lines.find(
      (line) => matchesCategory(line, category) && line.quantity_packed < line.quantity
    ) ?? null
  );
}

function buildDeployPayload(request: GearRequest, notes: string): DeployGearRequestPayload {
  const line_assets: Record<string, number | number[]> = {};
  const line_kits: Record<string, number> = {};
  const quantity_sent: Record<string, number> = {};

  for (const line of request.lines) {
    const assetIds: number[] = [];
    const seen = new Set<number>();

    if (line.selected_asset) {
      assetIds.push(line.selected_asset);
      seen.add(line.selected_asset);
    }

    for (const pa of line.packed_assets) {
      if (!seen.has(pa.asset)) {
        assetIds.push(pa.asset);
        seen.add(pa.asset);
      }
    }

    if (assetIds.length > 0) {
      quantity_sent[line.id] = Math.min(assetIds.length, line.quantity);
      line_assets[line.id] = assetIds.length === 1 ? assetIds[0] : assetIds;
    } else if (line.selected_kit) {
      // Kit-only line: the backend deploys the selected kit's assets.
      quantity_sent[line.id] = line.quantity;
    } else {
      quantity_sent[line.id] = 0;
    }

    if (line.selected_kit) {
      line_kits[line.id] = line.selected_kit;
    }
  }

  return {
    notes,
    line_assets,
    line_kits,
    quantity_sent,
  };
}

/**
 * True when the backend rejected a scan because the asset is already packed.
 *
 * The duplicate check on the server is scoped to a single gear request, so the
 * same physical asset would otherwise be accepted by the next candidate request
 * in the fallback loop and counted twice. The wording differs between the
 * asset-level rejection ("This asset has already been packed for this request.")
 * and the line-level one ("Line is already packed."), so match both.
 */
function isAlreadyPackedError(message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    normalized.includes('already been packed') || normalized.includes('already packed')
  );
}

export function GearBoardPage() {
  const permissions = usePermissions();
  const canViewBoard = permissions.canFulfillGearRequests;
  const canCreateGearRequest =
    permissions.isAdmin ||
    permissions.isManager ||
    permissions.isSupervisor ||
    permissions.getModuleRole('inventory') === 'supervisor';

  const [filters, setFilters] = useState<BoardFilters>(EMPTY_FILTERS);
  const [expandedProjects, setExpandedProjects] = useState<Set<number | null>>(new Set());

  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [deployModalOpen, setDeployModalOpen] = useState(false);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [adhocOpen, setAdhocOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmReject, setConfirmReject] = useState(false);

  const [activeNode, setActiveNode] = useState<GearBoardProjectNode | null>(null);
  const [activeCategory, setActiveCategory] = useState<GearBoardCategory | null>(null);

  const [assetIdentifier, setAssetIdentifier] = useState('');
  const [scanNotes, setScanNotes] = useState('');
  const [deployNotes, setDeployNotes] = useState('');
  const [receiveNotes, setReceiveNotes] = useState('');
  const [confirmNotes, setConfirmNotes] = useState('');
  const [deployWarnings, setDeployWarnings] = useState<string[]>([]);
  const [deployBlocked, setDeployBlocked] = useState(false);

  const [adhocProject, setAdhocProject] = useState('');
  const [adhocPriority, setAdhocPriority] = useState<GearRequestPriority>('normal');
  const [adhocNeededBy, setAdhocNeededBy] = useState('');
  const [adhocDescription, setAdhocDescription] = useState('');
  const [adhocCategory, setAdhocCategory] = useState<AssetType | ''>('');
  const [adhocQuantity, setAdhocQuantity] = useState('1');
  const [adhocNotes, setAdhocNotes] = useState('');

  const resetAdhocFields = useCallback(() => {
    setAdhocProject('');
    setAdhocPriority('normal');
    setAdhocNeededBy('');
    setAdhocDescription('');
    setAdhocCategory('');
    setAdhocQuantity('1');
    setAdhocNotes('');
  }, []);

  const openAdhoc = useCallback(() => {
    resetAdhocFields();
    setAdhocOpen(true);
  }, [resetAdhocFields]);

  const boardFilters = useMemo(
    () => ({
      project: filters.project ? parseInt(filters.project, 10) : ('' as const),
      status: filters.status,
      priority: filters.priority,
      needed_by_before: filters.needed_by_before,
      needed_by_after: filters.needed_by_after,
      board_status: filters.board_status,
    }),
    [filters]
  );

  const { data: boardData, isLoading: boardLoading, isError: boardError, refetch: refetchBoard } = useGearBoard(
    boardFilters,
    canViewBoard
  );

  const requestStatusFilter = filters.status
    ? filters.status
    : ACTIONABLE_REQUEST_STATUSES.join(',');

  const {
    data: requestsData,
    isLoading: requestsLoading,
    refetch: refetchRequests,
  } = useGearRequests(
    {
      page_size: 1000,
      project: filters.project ? parseInt(filters.project, 10) : '',
      status: requestStatusFilter,
      priority: filters.priority,
      needed_by_before: filters.needed_by_before,
      needed_by_after: filters.needed_by_after,
    },
    canViewBoard
  );

  const { data: projectsData } = useProjects({ page_size: 100 });

  const gearRequests = requestsData?.results ?? [];
  const boardNodes = boardData ?? [];
  const projects = projectsData?.results ?? [];

  const scanMutation = useScanGearRequest();
  const fulfillMutation = useFulfillGearRequest();
  const deployMutation = useDeployGearRequest();
  const receiveMutation = useReceiveGearRequest();
  const adhocMutation = useCreateGearRequest();
  const confirmMutation = useConfirmSupervisorRequest();

  const toggleProject = useCallback((projectId: number | null) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  }, []);

  const openScan = useCallback((node: GearBoardProjectNode, category: GearBoardCategory) => {
    setActiveNode(node);
    setActiveCategory(category);
    setAssetIdentifier('');
    setScanNotes('');
    setScanModalOpen(true);
  }, []);

  const openDeploy = useCallback((node: GearBoardProjectNode, category: GearBoardCategory) => {
    setActiveNode(node);
    setActiveCategory(category);
    setDeployNotes('');
    setDeployWarnings([]);
    setDeployBlocked(false);
    setDeployModalOpen(true);
  }, []);

  const openReceive = useCallback((node: GearBoardProjectNode, category: GearBoardCategory) => {
    setActiveNode(node);
    setActiveCategory(category);
    setReceiveNotes('');
    setReceiveModalOpen(true);
  }, []);

  const openConfirm = useCallback(
    (node: GearBoardProjectNode, category: GearBoardCategory, reject = false) => {
      setActiveNode(node);
      setActiveCategory(category);
      setConfirmReject(reject);
      setConfirmNotes('');
      setConfirmModalOpen(true);
    },
    []
  );

  const handleScan = async () => {
    if (!activeNode || !activeCategory) return;
    if (!assetIdentifier.trim()) {
      toast.error('Please enter an asset identifier');
      return;
    }

    const candidates = findMatchingRequests(
      gearRequests,
      activeNode,
      activeCategory,
      ['requested', 'fulfilled']
    );

    if (candidates.length === 0) {
      toast.error('No packable gear request found for this category');
      return;
    }

    let lastErrorMessage = 'No packable line found for this category';
    for (const request of candidates) {
      const targetLine = findMatchingLine(request, activeCategory);
      if (!targetLine) continue;

      try {
        await scanMutation.mutateAsync({
          id: request.id,
          data: {
            asset_identifier: assetIdentifier.trim(),
            line_id: targetLine.id,
            notes: scanNotes,
          },
        });
        toast.success('Asset scanned and packed');
        setScanModalOpen(false);
        setAssetIdentifier('');
        setScanNotes('');
        return;
      } catch (err: unknown) {
        const { message } = handleApiError(err);
        lastErrorMessage = message;
        if (isAlreadyPackedError(message)) {
          break;
        }
      }
    }

    toast.error(lastErrorMessage);
  };

  const handleDeploy = async () => {
    if (!activeNode || !activeCategory) return;

    const requests = findMatchingRequests(
      gearRequests,
      activeNode,
      activeCategory,
      ['requested', 'fulfilled']
    );

    if (requests.length === 0) {
      toast.error('No deployable gear request found for this category');
      return;
    }

    const warnings: string[] = [];
    // Tracks whether the backend actually checked any asset out, so the modal can
    // tell "deployed, with caveats" apart from "blocked, nothing happened".
    let assetsDeployed = false;

    try {
      for (const request of requests) {
        const matchingLines = request.lines.filter((line) =>
          matchesCategory(line, activeCategory)
        );
        const hasAssetsToSend = matchingLines.some(
          (line) =>
            line.quantity_packed > 0 ||
            line.selected_asset != null ||
            line.selected_kit != null
        );
        if (!hasAssetsToSend) {
          warnings.push(
            `Request #${request.id}: no assets packed for ${activeCategory?.description || 'this category'}; skipped.`
          );
          continue;
        }

        // Legacy requests created before this board existed pick an asset or kit
        // directly instead of scanning, so they have quantity_packed === 0 while
        // still being ready to ship. Treating those as unpacked would leave them
        // with a Deploy button that can never succeed.
        const otherUnpacked = request.lines.some(
          (line) =>
            !matchesCategory(line, activeCategory) &&
            line.quantity_sent < line.quantity &&
            line.quantity_packed < line.quantity &&
            line.selected_asset == null &&
            line.selected_kit == null
        );
        if (otherUnpacked) {
          warnings.push(
            `Request #${request.id}: other categories in this request are not fully packed. Pack them first or use the gear request detail page.`
          );
          continue;
        }

        if (request.status === 'requested') {
          await fulfillMutation.mutateAsync({ id: request.id });
        }

        const result = await deployMutation.mutateAsync({
          id: request.id,
          data: buildDeployPayload(request, deployNotes),
        });

        // Surface every warning the backend returned, verbatim. These used to be
        // filtered to messages containing the category description, which silently
        // dropped real failures ("<asset>: already has an active deployment",
        // "Failed to deploy <asset>") because those name the asset, not the category.
        const shortSends = result.short_sends || [];
        const substitutions = result.substitutions || [];

        shortSends.forEach((msg) =>
          warnings.push(`Request #${request.id}: Short send: ${msg}`)
        );
        substitutions.forEach((msg) =>
          warnings.push(`Request #${request.id}: ${msg}`)
        );

        // The backend omits this field entirely when it checked nothing out, so a
        // request can come back "successful" having shipped nothing at all.
        if ((result.deployments_created ?? 0) > 0) {
          assetsDeployed = true;
        } else {
          warnings.push(
            `Request #${request.id}: no assets were checked out, so nothing actually shipped.`
          );
        }

        if (result.short_send && shortSends.length === 0 && substitutions.length === 0) {
          warnings.push(`Request #${request.id}: short send`);
        }
      }

      if (warnings.length === 0) {
        toast.success('Gear deployed');
        setDeployModalOpen(false);
        return;
      }

      setDeployWarnings(warnings);
      setDeployBlocked(!assetsDeployed);
    } catch (err: unknown) {
      toast.error(handleApiError(err).message);
    }
  };

  const handleReceive = async () => {
    if (!activeNode || !activeCategory) return;

    const requests = findMatchingRequests(
      gearRequests,
      activeNode,
      activeCategory,
      ['in_transit', 'partially_received']
    );

    if (requests.length === 0) {
      toast.error('No gear in transit to receive for this category');
      return;
    }

    try {
      for (const request of requests) {
        const matchingLines = request.lines.filter((line) =>
          matchesCategory(line, activeCategory)
        );
        if (matchingLines.length === 0) continue;

        await receiveMutation.mutateAsync({
          id: request.id,
          data: {
            notes: receiveNotes,
            lines: matchingLines.map((line) => ({
              line_id: line.id,
              quantity_received: line.quantity_sent,
            })),
          },
        });
      }

      toast.success('Gear received');
      setReceiveModalOpen(false);
    } catch (err: unknown) {
      toast.error(handleApiError(err).message);
    }
  };

  const handleConfirm = async () => {
    if (!activeNode || !activeCategory) return;

    const requests = findMatchingRequests(
      gearRequests,
      activeNode,
      activeCategory,
      ['pending_confirmation']
    );

    if (requests.length === 0) {
      toast.error('No pending supervisor request found for this category');
      return;
    }

    try {
      for (const request of requests) {
        await confirmMutation.mutateAsync({
          id: request.id,
          data: { confirmed: !confirmReject, notes: confirmNotes },
        });
      }
      const action = confirmReject ? 'rejected' : 'confirmed';
      toast.success(
        requests.length === 1
          ? `Request ${action}`
          : `${requests.length} requests ${action}`
      );
      setConfirmModalOpen(false);
    } catch (err: unknown) {
      toast.error(handleApiError(err).message);
    }
  };

  const handleAdhocSubmit = async () => {
    if (!adhocProject || !adhocDescription || !adhocCategory) {
      toast.error('Please fill in project, description, and category');
      return;
    }

    const quantity = parseInt(adhocQuantity, 10);
    if (isNaN(quantity) || quantity < 1) {
      toast.error('Please enter a valid quantity');
      return;
    }

    try {
      const payload: CreateGearRequestPayload = {
        project: parseInt(adhocProject, 10),
        priority: adhocPriority,
        needed_by: adhocNeededBy || null,
        notes: adhocNotes,
        lines: [
          {
            description: adhocDescription,
            quantity,
            asset_category: adhocCategory,
            notes: adhocNotes,
          },
        ],
      };
      await adhocMutation.mutateAsync(payload);
      toast.success('Ad-hoc request submitted');
      setAdhocOpen(false);
      setAdhocProject('');
      setAdhocPriority('normal');
      setAdhocNeededBy('');
      setAdhocDescription('');
      setAdhocCategory('');
      setAdhocQuantity('1');
      setAdhocNotes('');
    } catch (err: unknown) {
      toast.error(handleApiError(err).message);
    }
  };

  const isLoading = boardLoading || requestsLoading;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-foreground">
            Gear Manager Board
          </h1>
          <p className="text-gray-600 dark:text-muted-foreground mt-1">
            Pack, deploy, and receive gear by project
          </p>
        </div>
        <div className="flex gap-2">
          {canViewBoard && (
            <Button
              variant="outline"
              onClick={() => {
                refetchBoard();
                refetchRequests();
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          )}
          {canViewBoard && (
            <>
              <Button variant="outline" asChild>
                <Link to="/inspections/batch">
                  <ClipboardCheck className="h-4 w-4 mr-2" />
                  Batch inspect
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/deployments/send-gear">
                  <Send className="h-4 w-4 mr-2" />
                  Send gear
                </Link>
              </Button>
            </>
          )}
          {canCreateGearRequest && (
            <Button onClick={openAdhoc}>
              <Plus className="h-4 w-4 mr-2" />
              Need More
            </Button>
          )}
        </div>
      </div>

      {canViewBoard && (
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row flex-wrap gap-4">
              <Select
                value={filters.project}
                onValueChange={(value) =>
                  setFilters((prev) => ({ ...prev, project: value === 'all' ? '' : value }))
                }
              >
                <SelectTrigger className="w-full sm:w-[220px]">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={String(project.id)}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.status}
                onValueChange={(value) =>
                  setFilters((prev) => ({ ...prev, status: value === 'all' ? '' : (value as GearRequestStatus) }))
                }
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Request Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Request Statuses</SelectItem>
                  <SelectItem value="pending_confirmation">Pending Confirmation</SelectItem>
                  <SelectItem value="requested">Requested</SelectItem>
                  <SelectItem value="fulfilled">Fulfilled</SelectItem>
                  <SelectItem value="in_transit">In Transit</SelectItem>
                  <SelectItem value="partially_received">Partially Received</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.board_status}
                onValueChange={(value) =>
                  setFilters((prev) => ({ ...prev, board_status: value === 'all' ? '' : (value as GearBoardStatus) }))
                }
              >
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Board Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Board Statuses</SelectItem>
                  <SelectItem value="pending_confirmation">Pending Confirmation</SelectItem>
                  <SelectItem value="requested">Requested</SelectItem>
                  <SelectItem value="packing">Packing</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                  <SelectItem value="in_transit">In Transit</SelectItem>
                  <SelectItem value="partially_received">Partially Received</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.priority}
                onValueChange={(value) =>
                  setFilters((prev) => ({ ...prev, priority: value === 'all' ? '' : value }))
                }
              >
                <SelectTrigger className="w-full sm:w-[160px]">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2">
                <Label htmlFor="needed-after" className="text-sm text-gray-600 whitespace-nowrap">
                  Needed after
                </Label>
                <Input
                  id="needed-after"
                  type="date"
                  value={filters.needed_by_after}
                  onChange={(e) => setFilters((prev) => ({ ...prev, needed_by_after: e.target.value }))}
                  className="w-full sm:w-[150px]"
                />
              </div>

              <div className="flex items-center gap-2">
                <Label htmlFor="needed-before" className="text-sm text-gray-600 whitespace-nowrap">
                  Needed before
                </Label>
                <Input
                  id="needed-before"
                  type="date"
                  value={filters.needed_by_before}
                  onChange={(e) => setFilters((prev) => ({ ...prev, needed_by_before: e.target.value }))}
                  className="w-full sm:w-[150px]"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {canViewBoard ? (
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : boardError ? (
              <div className="p-6 text-center text-red-600">
                Failed to load gear board. Please try again.
              </div>
            ) : boardNodes.length === 0 ? (
              <div className="p-12 text-center">
                <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">No gear categories match the current filters.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-border">
                {boardNodes.map((node) => (
                  <ProjectCard
                    key={node.project.id ?? 'none'}
                    node={node}
                    expanded={expandedProjects.has(node.project.id)}
                    onToggle={() => toggleProject(node.project.id)}
                    requests={gearRequests}
                    onScan={openScan}
                    onDeploy={openDeploy}
                    onReceive={openReceive}
                    onConfirm={openConfirm}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">
              Gear board is only available to managers. Use the "Need More" button to submit an ad-hoc request.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Scan Modal */}
      <Dialog open={scanModalOpen} onOpenChange={setScanModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Scan to Pack</DialogTitle>
            <DialogDescription>
              Scan a serial number or NFC tag to pack an asset for{' '}
              <strong>{activeCategory?.description}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="asset-identifier">Asset Identifier</Label>
              <Input
                id="asset-identifier"
                value={assetIdentifier}
                onChange={(e) => setAssetIdentifier(e.target.value)}
                placeholder="Serial number or NFC tag"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="scan-notes">Notes</Label>
              <Textarea
                id="scan-notes"
                value={scanNotes}
                onChange={(e) => setScanNotes(e.target.value)}
                placeholder="Optional scan notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScanModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleScan} disabled={scanMutation.isPending}>
              <ScanLine className="h-4 w-4 mr-2" />
              {scanMutation.isPending ? 'Scanning...' : 'Scan & Pack'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deploy Modal */}
      <Dialog open={deployModalOpen} onOpenChange={setDeployModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deploy Gear</DialogTitle>
            <DialogDescription>
              Deploy <strong>{activeCategory?.description}</strong>. The backend deploys the
              whole gear request, so any other packed lines in the same request will also be
              shipped. Deployment can be triggered even if some items are short.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="deploy-notes">Notes</Label>
              <Textarea
                id="deploy-notes"
                value={deployNotes}
                onChange={(e) => setDeployNotes(e.target.value)}
                placeholder="Optional deploy notes"
              />
            </div>
            {deployWarnings.length > 0 && (
              <div
                className={cn(
                  'rounded-md p-3 text-sm',
                  deployBlocked ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800'
                )}
              >
                <div className="flex items-center gap-2 font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  {deployBlocked ? 'Nothing was deployed' : 'Deployed with warnings'}
                </div>
                <ul className="mt-1 list-disc list-inside">
                  {deployWarnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeployModalOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="deploy-submit"
              onClick={deployWarnings.length > 0 ? () => setDeployModalOpen(false) : handleDeploy}
              disabled={deployMutation.isPending || fulfillMutation.isPending}
            >
              <Truck className="h-4 w-4 mr-2" />
              {deployWarnings.length > 0
                ? deployBlocked
                  ? 'Close'
                  : 'Done'
                : deployMutation.isPending || fulfillMutation.isPending
                ? 'Deploying...'
                : 'Deploy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Modal */}
      <Dialog open={receiveModalOpen} onOpenChange={setReceiveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Receive Gear</DialogTitle>
            <DialogDescription>
              Mark <strong>{activeCategory?.description}</strong> as received at site.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="receive-notes">Notes</Label>
              <Textarea
                id="receive-notes"
                value={receiveNotes}
                onChange={(e) => setReceiveNotes(e.target.value)}
                placeholder="Optional receipt notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleReceive} disabled={receiveMutation.isPending}>
              <Inbox className="h-4 w-4 mr-2" />
              {receiveMutation.isPending ? 'Receiving...' : 'Receive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm/Reject Modal */}
      <Dialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirmReject ? 'Reject Request' : 'Confirm Request'}</DialogTitle>
            <DialogDescription>
              {confirmReject ? 'Reject' : 'Confirm'} the ad-hoc request for{' '}
              <strong>{activeCategory?.description}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="confirm-notes">Notes</Label>
              <Textarea
                id="confirm-notes"
                value={confirmNotes}
                onChange={(e) => setConfirmNotes(e.target.value)}
                placeholder="Optional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant={confirmReject ? 'destructive' : 'default'}
              onClick={handleConfirm}
              disabled={confirmMutation.isPending}
            >
              {confirmMutation.isPending
                ? confirmReject
                  ? 'Rejecting...'
                  : 'Confirming...'
                : confirmReject
                  ? 'Reject'
                  : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ad-hoc Request Modal */}
      <Dialog open={adhocOpen} onOpenChange={setAdhocOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Request More Gear</DialogTitle>
            <DialogDescription>
              Submit an ad-hoc gear request for a project and category.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="adhoc-project">Project</Label>
              <Select value={adhocProject} onValueChange={setAdhocProject}>
                <SelectTrigger id="adhoc-project" className="w-full">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={String(project.id)}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="adhoc-priority">Priority</Label>
                <Select
                  value={adhocPriority}
                  onValueChange={(value) => setAdhocPriority(value as GearRequestPriority)}
                >
                  <SelectTrigger id="adhoc-priority" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="adhoc-needed-by">Needed By</Label>
                <Input
                  id="adhoc-needed-by"
                  type="date"
                  value={adhocNeededBy}
                  onChange={(e) => setAdhocNeededBy(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adhoc-description">Description</Label>
              <Input
                id="adhoc-description"
                value={adhocDescription}
                onChange={(e) => setAdhocDescription(e.target.value)}
                placeholder="e.g. 11mm static rope 60m"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="adhoc-category">Category</Label>
                <Select value={adhocCategory} onValueChange={(value) => setAdhocCategory(value as AssetType)}>
                  <SelectTrigger id="adhoc-category" className="w-full">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {assetCategoryOptions.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {formatCategory(cat)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="adhoc-quantity">Quantity</Label>
                <Input
                  id="adhoc-quantity"
                  type="number"
                  min={1}
                  value={adhocQuantity}
                  onChange={(e) => setAdhocQuantity(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="adhoc-notes">Notes</Label>
              <Textarea
                id="adhoc-notes"
                value={adhocNotes}
                onChange={(e) => setAdhocNotes(e.target.value)}
                placeholder="Optional notes for the request"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdhocOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdhocSubmit} disabled={adhocMutation.isPending}>
              {adhocMutation.isPending ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface ProjectCardProps {
  node: GearBoardProjectNode;
  expanded: boolean;
  onToggle: () => void;
  requests: GearRequest[];
  onScan: (node: GearBoardProjectNode, category: GearBoardCategory) => void;
  onDeploy: (node: GearBoardProjectNode, category: GearBoardCategory) => void;
  onReceive: (node: GearBoardProjectNode, category: GearBoardCategory) => void;
  onConfirm: (node: GearBoardProjectNode, category: GearBoardCategory, reject: boolean) => void;
}

function ProjectCard({
  node,
  expanded,
  onToggle,
  requests,
  onScan,
  onDeploy,
  onReceive,
  onConfirm,
}: ProjectCardProps) {
  const totalItems = node.categories.reduce((sum, c) => sum + c.total, 0);
  const totalShortage = node.categories.reduce((sum, c) => sum + c.shortage, 0);

  return (
    <Collapsible open={expanded} onOpenChange={onToggle}>
      <CollapsibleTrigger asChild>
        <button
          className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-accent transition-colors"
          data-testid="project-card-header"
        >
          <div className="flex items-center gap-3">
            {expanded ? (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronRight className="h-5 w-5 text-gray-400" />
            )}
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-foreground">
                {node.project.name}
              </h3>
              <p className="text-sm text-gray-500 dark:text-muted-foreground">
                {node.categories.length} categor{node.categories.length === 1 ? 'y' : 'ies'} · {totalItems} items
                {totalShortage > 0 && (
                  <span className="ml-2 text-amber-600">
                    ({totalShortage} short)
                  </span>
                )}
              </p>
            </div>
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pb-4">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-muted text-gray-500 dark:text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Category</th>
                  <th className="px-3 py-2 text-left font-medium">Description</th>
                  <th className="px-3 py-2 text-right font-medium">Total</th>
                  <th className="px-3 py-2 text-right font-medium">Packed</th>
                  <th className="px-3 py-2 text-right font-medium">Sent</th>
                  <th className="px-3 py-2 text-right font-medium">Received</th>
                  <th className="px-3 py-2 text-center font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-border">
                {node.categories.map((category, idx) => (
                  <CategoryRow
                    key={`${category.description}-${idx}`}
                    node={node}
                    category={category}
                    requests={requests}
                    onScan={onScan}
                    onDeploy={onDeploy}
                    onReceive={onReceive}
                    onConfirm={onConfirm}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

interface CategoryRowProps {
  node: GearBoardProjectNode;
  category: GearBoardCategory;
  requests: GearRequest[];
  onScan: (node: GearBoardProjectNode, category: GearBoardCategory) => void;
  onDeploy: (node: GearBoardProjectNode, category: GearBoardCategory) => void;
  onReceive: (node: GearBoardProjectNode, category: GearBoardCategory) => void;
  onConfirm: (node: GearBoardProjectNode, category: GearBoardCategory, reject: boolean) => void;
}

function CategoryRow({
  node,
  category,
  requests,
  onScan,
  onDeploy,
  onReceive,
  onConfirm,
}: CategoryRowProps) {
  const permissions = usePermissions();
  const canManage = permissions.canFulfillGearRequests;

  const scannable = ['requested', 'packing'].includes(category.status);
  const deployable = ['packing', 'ready'].includes(category.status);
  const receivable = ['in_transit', 'partially_received'].includes(category.status);
  const confirmable = category.status === 'pending_confirmation';

  // Mirrors handleScan, which packs via findMatchingLine and so needs a line with
  // room left. Without the quantity check the button shows on a fully packed
  // category and the click only produces an error toast.
  const hasScannableMatch = findMatchingRequests(
    requests,
    node,
    category,
    ['requested', 'fulfilled']
  ).some((request) => findMatchingLine(request, category) !== null);
  const hasDeployableMatch =
    findFirstMatchingRequest(requests, node, category, ['requested', 'fulfilled']) !== null;
  const hasReceivableMatch =
    findFirstMatchingRequest(requests, node, category, ['in_transit', 'partially_received']) !==
    null;
  const hasConfirmableMatch =
    findFirstMatchingRequest(requests, node, category, ['pending_confirmation']) !== null;

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-accent">
      <td className="px-3 py-3 align-middle">
        {category.asset_category ? formatCategory(category.asset_category) : '—'}
      </td>
      <td className="px-3 py-3 align-middle">
        <div>
          <div className="font-medium text-gray-900 dark:text-foreground">
            {category.description}
          </div>
          {category.gear_model.name && (
            <div className="text-xs text-gray-500 dark:text-muted-foreground">
              {category.gear_model.name}
            </div>
          )}
        </div>
      </td>
      <td className="px-3 py-3 align-middle text-right">{category.total}</td>
      <td className="px-3 py-3 align-middle text-right">{category.packed}</td>
      <td className="px-3 py-3 align-middle text-right">{category.sent}</td>
      <td className="px-3 py-3 align-middle text-right">{category.received}</td>
      <td className="px-3 py-3 align-middle text-center">
        <Badge className={cn(boardStatusColors[category.status])}>
          {boardStatusLabels[category.status]}
        </Badge>
        {category.shortage > 0 && (
          <span className="block text-xs text-amber-600 mt-1">
            {category.shortage} short
          </span>
        )}
      </td>
      <td className="px-3 py-3 align-middle">
        <div className="flex items-center gap-1 flex-wrap">
          {confirmable && canManage && hasConfirmableMatch && (
            <>
              <Button size="sm" variant="outline" onClick={() => onConfirm(node, category, false)}>
                Confirm
              </Button>
              <Button size="sm" variant="outline" onClick={() => onConfirm(node, category, true)}>
                Reject
              </Button>
            </>
          )}
          {scannable && canManage && hasScannableMatch && (
            <Button size="sm" variant="outline" onClick={() => onScan(node, category)}>
              <QrCode className="h-3.5 w-3.5 mr-1" />
              Scan
            </Button>
          )}
          {deployable && canManage && hasDeployableMatch && (
            <Button size="sm" variant="outline" onClick={() => onDeploy(node, category)}>
              <Truck className="h-3.5 w-3.5 mr-1" />
              Deploy
            </Button>
          )}
          {receivable && canManage && hasReceivableMatch && (
            <Button size="sm" variant="outline" onClick={() => onReceive(node, category)}>
              <Inbox className="h-3.5 w-3.5 mr-1" />
              Receive
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}
